import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { Bell, Loader, Plus, Type, Image as ImageIcon, Link as LinkIcon } from 'lucide-react'
import { NewsroomBlock } from './components/NewsroomBlock'
import { NotificationSettingsModal } from './components/NotificationSettingsModal'
import { NewsroomComposer } from './NewsroomComposer'

type ComposeType = 'text' | 'photo' | 'link'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface Block {
  id: string
  block_type: string
  content: any
  layout_config: {
    gridSpan: number
    customHeight: number | null
    mobileSpan: number
  }
  s3_key?: string
  file_url?: string
  thumbnail_url?: string
  like_count: number
  user_liked: boolean
  options?: any[]
  user_voted_option?: string
}

export default function NewsroomPage() {
  const [blocks, setBlocks] = useState<Block[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const [showNotificationSettings, setShowNotificationSettings] = useState(false)
  const [showComposerPanel, setShowComposerPanel] = useState(false)
  const [composeIntent, setComposeIntent] = useState<ComposeType | null>(null)

  // Open the composer straight into the compose form (entry-card style).
  const openComposer = useCallback((type: ComposeType = 'text') => {
    setComposeIntent(type)
    setShowComposerPanel(true)
  }, [])
  // Open the panel on its "your posts" list view (header button).
  const openPanel = useCallback(() => {
    setComposeIntent(null)
    setShowComposerPanel(true)
  }, [])
  const closePanel = useCallback(() => {
    setShowComposerPanel(false)
    setComposeIntent(null)
  }, [])

  const limit = 20

  // Get auth token
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('auth_token')
  }, [])

  // Load blocks
  const loadBlocks = useCallback(async (reset = false) => {
    try {
      setIsLoading(true)

      const currentOffset = reset ? 0 : offset
      const token = getAuthToken()

      const headers: Record<string, string> = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(
        `${API_URL}/api/newsroom/blocks?limit=${limit}&offset=${currentOffset}`,
        { headers }
      )

      if (!response.ok) {
        throw new Error('Failed to load newsroom')
      }

      const result = await response.json()

      if (reset) {
        setBlocks(result.data)
        setOffset(limit)
      } else {
        setBlocks(prev => [...prev, ...result.data])
        setOffset(prev => prev + limit)
      }

      setHasMore(result.pagination.hasMore)
    } catch (error) {
      console.error('Load blocks error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [offset, getAuthToken])

  // Initial load
  useEffect(() => {
    loadBlocks(true)
  }, [])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (isLoading || !hasMore) return

    const scrollHeight = document.documentElement.scrollHeight
    const scrollTop = document.documentElement.scrollTop
    const clientHeight = window.innerHeight

    if (scrollTop + clientHeight >= scrollHeight - 500) {
      loadBlocks()
    }
  }, [isLoading, hasMore, loadBlocks])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F8FF] to-[#D4EEFF]">
      {/* Header */}
      <div className="bg-white border-b border-[#D4EEFF] sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Updates & News</h1>
            </div>

            <div className="flex items-center gap-2">
              {/* Open to every subscriber — the newsroom route is subscription-gated,
                  so anyone viewing it can submit a post for review. */}
              <button
                onClick={openPanel}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-lg transition-colors"
                aria-label="Add a post"
              >
                <Plus size={16} />
                Post
              </button>
              <button
                onClick={() => setShowNotificationSettings(true)}
                className="p-2 hover:bg-[#F0F8FF] rounded-lg transition-colors"
                aria-label="Notification settings"
              >
                <Bell size={20} className="text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Blocks Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Composer entry — the inviting "share something" hook, open to all subscribers */}
        <ComposerEntry onOpen={openComposer} className="mb-4" />

        {blocks.length === 0 && !isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-600">No updates yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            {blocks.map((block) => (
              <NewsroomBlock key={block.id} block={block} />
            ))}
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="py-8 flex justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader className="animate-spin text-[#4AA3CC]" size={32} />
              <p className="text-sm text-gray-600">Loading more updates...</p>
            </div>
          </div>
        )}

        {/* End of content */}
        {!hasMore && blocks.length > 0 && (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-500">You're all caught up!</p>
          </div>
        )}
      </div>

      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <NotificationSettingsModal
          onClose={() => setShowNotificationSettings(false)}
        />
      )}

      {/* Composer panel — opened from the header Post button or the feed entry card */}
      <NewsroomComposer
        open={showComposerPanel}
        onClose={closePanel}
        startCompose={composeIntent !== null}
        initialType={composeIntent ?? 'text'}
      />
    </div>
  )
}

/**
 * Inviting composer entry pinned to the top of the feed — reads like a
 * "What's on your mind?" box. The whole pill opens the composer; the
 * Text / Photo / Link chips open it pre-set to that post type.
 */
function ComposerEntry({ onOpen, className = '' }: { onOpen: (type: ComposeType) => void; className?: string }) {
  return (
    <div className={`bg-white rounded-2xl border border-[#D4EEFF] shadow-sm p-3 sm:p-4 ${className}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#87CEEB] to-[#4AA3CC] flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0">
          M
        </div>
        <button
          data-testid="composer-entry"
          onClick={() => onOpen('text')}
          className="flex-1 text-left px-4 py-2.5 rounded-full bg-[#F0F8FF] hover:bg-[#D4EEFF] text-gray-500 text-sm sm:text-base transition-colors"
        >
          Share something with the Mini's World Community…
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1 mt-3 pt-3 border-t border-gray-100">
        <EntryChip icon={<Type size={17} className="text-[#4AA3CC]" />} label="Text" onClick={() => onOpen('text')} />
        <EntryChip icon={<ImageIcon size={17} className="text-[#4AA3CC]" />} label="Photo" onClick={() => onOpen('photo')} />
        <EntryChip icon={<LinkIcon size={17} className="text-[#4AA3CC]" />} label="Link" onClick={() => onOpen('link')} />
      </div>
    </div>
  )
}

function EntryChip({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-[#F0F8FF] transition-colors"
    >
      {icon}
      {label}
    </button>
  )
}
